import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Loader2, Plus } from "lucide-react";
import { API_URL } from "../data/site";

const inputCls =
  "w-full border border-neutral-300 bg-white px-4 py-3 text-sm text-[#111111] placeholder:text-neutral-400 transition-colors duration-300 focus:border-[#111111] focus:outline-none";
const labelCls = "font-overline mb-2 block text-[10px] uppercase tracking-[0.25em] text-neutral-500";
const EMPTY = { client_name: "", client_email: "", description: "", total: "", deposit: "" };

export const money = (n) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const when = (iso) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const STATUS = {
  open: "bg-[#f8f9fa] text-neutral-600",
  deposit_paid: "bg-amber-50 text-amber-700",
  paid: "bg-emerald-50 text-emerald-700",
};

export const StatusPill = ({ value }) => (
  <span className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] ${STATUS[value] || "bg-[#f8f9fa] text-neutral-600"}`}>
    {value.replace("_", " ")}
  </span>
);

export const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/quotes`, { withCredentials: true });
      setQuotes(res.data);
    } catch {
      toast.error("Could not load quotes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, total: Number(form.total), deposit: form.deposit ? Number(form.deposit) : null };
      const res = await axios.post(`${API_URL}/quotes`, payload, { withCredentials: true });
      toast.success(`Quote ${res.data.reference} saved. Share the reference with ${res.data.client_name}.`);
      setForm(EMPTY);
      setOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save the quote.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-quotes">
      <button
        type="button"
        data-testid="quote-form-toggle"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 border border-neutral-300 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#111111] transition-colors duration-300 hover:border-[#111111]"
      >
        <Plus className="h-4 w-4 text-[#e63946]" />
        {open ? "Close quote form" : "New quote"}
      </button>

      {open && (
        <form onSubmit={submit} data-testid="quote-form" className="mt-6 space-y-5 border border-neutral-200 bg-[#f8f9fa] p-6 md:p-8">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="quote-client-name" className={labelCls}>Client name *</label>
              <input id="quote-client-name" data-testid="quote-client-name-input" required minLength={2} value={form.client_name} onChange={update("client_name")} placeholder="Jordan Driver" className={inputCls} />
            </div>
            <div>
              <label htmlFor="quote-client-email" className={labelCls}>Client email *</label>
              <input id="quote-client-email" data-testid="quote-client-email-input" type="email" required value={form.client_email} onChange={update("client_email")} placeholder="driver@example.com" className={inputCls} />
            </div>
          </div>
          <div>
            <label htmlFor="quote-description" className={labelCls}>Program description *</label>
            <input id="quote-description" data-testid="quote-description-input" required minLength={4} maxLength={400} value={form.description} onChange={update("description")} placeholder="Road Atlanta race weekend — arrive & drive, coaching, trackside support" className={inputCls} />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="quote-total" className={labelCls}>Quote total (USD) *</label>
              <input id="quote-total" data-testid="quote-total-input" type="number" min="1" max="50000" step="0.01" required value={form.total} onChange={update("total")} placeholder="0.00" className={inputCls} />
            </div>
            <div>
              <label htmlFor="quote-deposit" className={labelCls}>Optional deposit (USD)</label>
              <input id="quote-deposit" data-testid="quote-deposit-input" type="number" min="1" max="50000" step="0.01" value={form.deposit} onChange={update("deposit")} placeholder="Leave blank for full payment only" className={inputCls} />
            </div>
          </div>
          <button type="submit" data-testid="quote-save-button" disabled={saving} className="inline-flex items-center gap-2 bg-[#111111] px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-[#e63946] disabled:cursor-not-allowed disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            Save quote
          </button>
        </form>
      )}

      <div data-testid="quotes-list" className="mt-8">
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-[#e63946]" />
        ) : quotes.length === 0 ? (
          <div className="flex flex-col items-center gap-4 border border-dashed border-neutral-300 py-20 text-neutral-400">
            <FileText className="h-8 w-8" />
            <p className="text-sm">No quotes yet. Create one and share its reference with the client.</p>
          </div>
        ) : (
          quotes.map((q, i) => (
            <article key={q.id} data-testid={`quote-item-${i}`} className="border-t border-neutral-200 py-5">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span data-testid={`quote-reference-${i}`} className="font-overline text-[11px] tracking-[0.25em] text-[#e63946]">{q.reference}</span>
                  <h3 className="font-display text-lg font-bold tracking-tight text-[#111111]">{q.client_name}</h3>
                  <StatusPill value={q.status} />
                </div>
                <time className="text-xs uppercase tracking-[0.15em] text-neutral-400">{when(q.created_at)}</time>
              </div>
              <p className="mt-1 text-sm text-neutral-600">{q.client_email}</p>
              <p className="mt-2 text-sm leading-relaxed text-neutral-700">{q.description}</p>
              <p className="mt-2 text-sm text-[#111111]">
                <span className="font-semibold">{money(q.total_cents / 100)}</span> total
                {q.deposit_cents ? <span className="text-neutral-500"> · {money(q.deposit_cents / 100)} deposit option</span> : null}
              </p>
            </article>
          ))
        )}
      </div>
    </div>
  );
};
